import { db, rtdb } from '@/lib/firebase';
import { collection, addDoc, doc, updateDoc, query, where, onSnapshot, orderBy, limit, Timestamp, serverTimestamp, getDoc, getDocs } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { EmailAPI } from './emailAPI';

export type NotificationType = 
  | 'new_reaction'
  | 'new_comment'
  | 'new_message'
  | 'friend_request'
  | 'friend_accepted'
  | 'new_post'
  | 'profile_post'
  | 'post_mention'
  | 'comment_reply'
  | 'post_share'
  | 'achievement'
  | 'call'
  | 'group_invite'
  | 'page_invite'
  | 'system';

interface NotificationData {
  postId?: string;
  commentId?: string;
  messageId?: string;
  callId?: string;
  friendshipId?: string;
  senderId?: string;
  senderName?: string;
  senderPhoto?: string;
  senderUsername?: string;
  reactorName?: string;
  reactorPhoto?: string;
  commenterName?: string;
  commenterPhoto?: string;
  authorName?: string;
  authorPhoto?: string;
  authorUsername?: string;
  accepterId?: string;
  accepterName?: string;
  accepterPhoto?: string;
  accepterUsername?: string;
  reactionType?: string;
  commentText?: string;
  postMediaType?: string; // NEW: Type of media in the post (image, video, etc.)
  achievementType?: string;
  milestone?: number;
  title?: string;
  message?: string;
  messagePreview?: string;
  callType?: 'audio' | 'video';
  groupId?: string;
  groupName?: string;
  pageId?: string;
  pageName?: string;
  actionUrl?: string;
}

interface CreateNotificationParams {
  userId: string;
  type: NotificationType;
  data: NotificationData;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

interface NotificationRecord {
  id: string;
  userId: string;
  type: NotificationType;
  title?: string;
  message?: string;
  read?: boolean;
  createdAt?: string;
  data?: NotificationData;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

/**
 * Enhanced notification service with real-time support
 */
class NotificationService {
  private listeners: Map<string, () => void> = new Map();
  private readonly dedupeWindowMs = 10 * 60 * 1000;

  private isPermissionDeniedError(error: unknown): boolean {
    const code = String((error as any)?.code || '').toLowerCase();
    const message = String((error as any)?.message || '').toLowerCase();
    return code.includes('permission-denied') || message.includes('insufficient permissions');
  }

  private coerceString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private normalizeData(type: NotificationType, data: NotificationData): NotificationData {
    const normalized: NotificationData = { ...data };

    const postId = this.coerceString(normalized.postId);
    const commentId = this.coerceString(normalized.commentId);
    const senderId = this.coerceString(normalized.senderId);
    const senderUsername = this.coerceString((normalized as any).senderUsername);
    const accepterUsername = this.coerceString((normalized as any).accepterUsername);
    const callId = this.coerceString((normalized as any).callId);

    // Canonical post deep-link
    if (postId) {
      normalized.actionUrl = `/post/${encodeURIComponent(postId)}${commentId ? `?comment=${encodeURIComponent(commentId)}` : ''}`;
      return normalized;
    }

    // Canonical message deep-link
    if (type === 'new_message' && senderId) {
      const messageUser = senderUsername || senderId;
      normalized.actionUrl = `/messages?user=${encodeURIComponent(messageUser)}`;
      return normalized;
    }

    // Canonical call deep-link
    if (type === 'call' && callId) {
      normalized.actionUrl = `/calls?call=${encodeURIComponent(callId)}`;
      return normalized;
    }

    // Canonical profile deep-link (only possible when we have username)
    if ((type === 'friend_request' || type === 'post_share') && senderUsername) {
      normalized.actionUrl = `/profile/${encodeURIComponent(senderUsername)}`;
      return normalized;
    }
    if (type === 'friend_accepted' && accepterUsername) {
      normalized.actionUrl = `/profile/${encodeURIComponent(accepterUsername)}`;
      return normalized;
    }

    return normalized;
  }

  private getDedupeKey(type: NotificationType, data: NotificationData): string {
    const senderId = this.coerceString(data.senderId) || this.coerceString(data.accepterId) || 'unknown';
    const postId = this.coerceString(data.postId) || 'none';
    const commentId = this.coerceString(data.commentId) || 'none';
    const messageId = this.coerceString(data.messageId) || 'none';
    const callId = this.coerceString(data.callId) || 'none';
    const groupId = this.coerceString(data.groupId) || 'none';
    const pageId = this.coerceString(data.pageId) || 'none';

    return [type, senderId, postId, commentId, messageId, callId, groupId, pageId].join('|');
  }

  private parseCreatedAt(value: unknown): Date | null {
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isFinite(parsed.getTime()) ? parsed : null;
    }

    if (value instanceof Timestamp) {
      return value.toDate();
    }

    if (value instanceof Date) {
      return Number.isFinite(value.getTime()) ? value : null;
    }

    return null;
  }

  private async findCollapsibleNotification(
    userId: string,
    type: NotificationType,
    dedupeKey: string
  ): Promise<NotificationRecord | null> {
    try {
      const recentQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('type', '==', type),
        where('read', '==', false),
        orderBy('createdAt', 'desc'),
        limit(8)
      );

      const snapshot = await getDocs(recentQuery);
      const now = Date.now();

      for (const snapshotDoc of snapshot.docs) {
        const record = { id: snapshotDoc.id, ...(snapshotDoc.data() as Record<string, unknown>) } as NotificationRecord;
        const createdAt = this.parseCreatedAt(record.createdAt);
        if (!createdAt) continue;

        if (now - createdAt.getTime() > this.dedupeWindowMs) {
          continue;
        }

        const existingKey = this.getDedupeKey(type, (record.data || {}) as NotificationData);
        if (existingKey === dedupeKey) {
          return record;
        }
      }
    } catch (error) {
      // Dedupe should never block notification creation.
      console.warn('[NotificationService] Dedupe query skipped, creating standalone notification:', error);
    }

    return null;
  }

  private computeUrgency(notification: any): { priorityScore: number; urgency: 'low' | 'medium' | 'high' } {
    const baseByType: Record<string, number> = {
      new_message: 14,
      call: 13,
      friend_request: 11,
      post_mention: 10,
      comment_reply: 9,
      new_comment: 8,
      new_reaction: 6,
      achievement: 5,
      system: 4,
    };

    const createdAt = this.parseCreatedAt(notification.createdAt);
    const ageHours = createdAt
      ? Math.max(0, (Date.now() - createdAt.getTime()) / (1000 * 60 * 60))
      : 24;
    const recencyBoost = Math.max(0, 10 - ageHours / 2);
    const unreadBoost = notification.read ? 0 : 5;
    const collapsedCount = Number(notification.collapsedCount || 1);
    const frequencyBoost = Math.min(6, Math.max(0, collapsedCount - 1));

    const priorityScore = Math.round((baseByType[notification.type] ?? 4) + recencyBoost + unreadBoost + frequencyBoost);
    const urgency: 'low' | 'medium' | 'high' = priorityScore >= 22 ? 'high' : priorityScore >= 14 ? 'medium' : 'low';

    return { priorityScore, urgency };
  }

  /**
   * Create a new notification
   */
  async createNotification({
    userId,
    type,
    data,
    priority = 'normal'
  }: CreateNotificationParams): Promise<string> {
    try {
      const normalizedData = this.normalizeData(type, data);
      const dedupeKey = this.getDedupeKey(type, normalizedData);

      const { title, message } = this.getNotificationContent(type, normalizedData);

      // Clean data to remove undefined values (Firestore doesn't accept undefined)
      const cleanData = Object.fromEntries(
        Object.entries(normalizedData).filter(([_, value]) => value !== undefined)
      );

      const collapsible = await this.findCollapsibleNotification(userId, type, dedupeKey);
      if (collapsible) {
        const nextCollapsedCount = Math.max(1, Number((collapsible as any).collapsedCount || 1) + 1);
        const mergedMessage = nextCollapsedCount > 1 ? `${message} (${nextCollapsedCount}x)` : message;

        await updateDoc(doc(db, 'notifications', collapsible.id), {
          title,
          message: mergedMessage,
          data: cleanData,
          priority,
          collapsedCount: nextCollapsedCount,
          lastTriggeredAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          timestamp: serverTimestamp(),
          read: false,
        });

        return collapsible.id;
      }

      // Create notification object
      const notificationData = {
        userId,
        type,
        title,
        message,
        data: cleanData,
        read: false,
        priority,
        collapsedCount: 1,
        createdAt: new Date().toISOString(),
        timestamp: serverTimestamp()
      };

      // Add to Firestore for persistence
      const notificationRef = await addDoc(collection(db, 'notifications'), notificationData);

      // RTDB sync is handled server-side (Firestore triggers) for security.

      // Trigger push notification (if enabled)
      this.triggerPushNotification(userId, { title, message, data: cleanData });

      // Send email notification (async, don't wait)
      this.sendEmailNotification(userId, type, cleanData, { title, message }).catch(error => {
        console.error('[NotificationService] Email notification failed (non-critical):', error);
      });

      return notificationRef.id;
    } catch (error) {
      console.error('[NotificationService] Error creating notification:', error);
      throw error;
    }
  }

  /**
   * Get notification content based on type
   */
  private getNotificationContent(type: NotificationType, data: NotificationData) {
    switch (type) {
      case 'new_reaction':
        const mediaTypeText = data.postMediaType === 'video' ? 'video' : data.postMediaType === 'image' ? 'photo' : 'post';
        return {
          title: 'New Reaction',
          message: `${data.reactorName || 'Someone'} ${data.reactionType || 'liked'} your ${mediaTypeText}`
        };
      case 'new_comment':
        const commentMediaType = data.postMediaType === 'video' ? 'video' : data.postMediaType === 'image' ? 'photo' : 'post';
        return {
          title: 'New Comment',
          message: `${data.commenterName || 'Someone'} commented on your ${commentMediaType}: "${data.commentText?.substring(0, 50) || 'View comment'}${data.commentText && data.commentText.length > 50 ? '...' : ''}"`
        };
      case 'comment_reply':
        return {
          title: 'Comment Reply',
          message: `${data.commenterName || 'Someone'} replied to your comment: "${data.commentText?.substring(0, 50) || 'View reply'}${data.commentText && data.commentText.length > 50 ? '...' : ''}"`
        };
      case 'new_message':
        return {
          title: 'New Message',
          message: `${data.senderName || 'Someone'} sent you a message`
        };
      case 'friend_request':
        return {
          title: 'Friend Request',
          message: `${data.senderName || 'Someone'} sent you a friend request`
        };
      case 'friend_accepted':
        return {
          title: 'Friend Request Accepted',
          message: `${data.accepterName || 'Someone'} accepted your friend request`
        };
      case 'new_post':
        return {
          title: 'New Post',
          message: `${data.authorName || 'Someone'} shared a new post`
        };
      case 'profile_post':
        return {
          title: 'Posted on Your Profile',
          message: `${data.authorName || 'Someone'} posted on your profile`
        };
      case 'post_mention':
        return {
          title: 'You Were Mentioned',
          message: `${data.authorName || 'Someone'} mentioned you in a post`
        };
      case 'post_share':
        return {
          title: 'Post Shared',
          message: `${data.senderName || 'Someone'} shared your post`
        };
      case 'achievement':
        return {
          title: 'Achievement Unlocked! 🎉',
          message: `Congratulations! You've reached ${data.milestone} ${data.achievementType}!`
        };
      case 'call':
        const callTypeText = data.callType === 'video' ? 'video call' : 'audio call';
        return {
          title: 'Missed Call',
          message: `${data.senderName || 'Someone'} called you (${callTypeText})`
        };
      case 'group_invite':
        return {
          title: 'Group Invite',
          message: `${data.senderName || 'Someone'} invited you to join ${data.groupName || 'a group'}`,
        };
      case 'page_invite':
        return {
          title: 'Page Invite',
          message: `${data.senderName || 'Someone'} invited you to follow ${data.pageName || 'a page'}`,
        };
      case 'system':
        return {
          title: 'System Notification',
          message: data.commenterName || 'You have a system notification'
        };
      default:
        return {
          title: 'New Notification',
          message: 'You have a new notification'
        };
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(userId: string, notificationId: string): Promise<void> {
    try {
      // Update in Firestore
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('[NotificationService] Error marking as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const snapshot = await new Promise<any>((resolve, reject) => {
        const unsubscribe = onSnapshot(notificationsQuery, resolve, reject);
        return unsubscribe;
      });

      const updates = snapshot.docs.map((doc: any) =>
        updateDoc(doc.ref, {
          read: true,
          readAt: new Date().toISOString()
        })
      );

      await Promise.all(updates);
    } catch (error) {
      console.error('[NotificationService] Error marking all as read:', error);
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(userId: string, notificationId: string): Promise<void> {
    try {
      // Remove from Firestore
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, { deleted: true });
    } catch (error) {
      console.error('[NotificationService] Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Get unread count
   */
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );

      return new Promise((resolve) => {
        const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
          unsubscribe();
          resolve(snapshot.size);
        }, (error) => {
          unsubscribe();
          if (!this.isPermissionDeniedError(error)) {
            console.error('[NotificationService] Error getting unread count:', error);
          }
          resolve(0);
        });
      });
    } catch (error) {
      console.error('[NotificationService] Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Listen to real-time notifications
   */
  listenToNotifications(
    userId: string,
    callback: (notifications: any[]) => void
  ): () => void {
    // Simple query without deleted filter to ensure it works
    // We'll filter deleted notifications in memory instead
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribeFirestore = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        try {
          const notifications = snapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
              timestamp: doc.data().createdAt?.toDate?.()
            }))
            .filter((notif: any) => !notif.deleted)
            .map((notif: any) => {
              const urgency = this.computeUrgency(notif);
              return {
                ...notif,
                priorityScore: urgency.priorityScore,
                urgency: urgency.urgency,
              };
            }); // Extra safety filter

          callback(notifications);
        } catch (error) {
          console.error('[NotificationService] Error processing notifications:', error);
        }
      },
      (error) => {
        if (!this.isPermissionDeniedError(error)) {
          console.error('[NotificationService] Firestore listener error:', error);
        }
        callback([]);
      }
    );

    // Realtime Database listener (for instant updates)
    let unsubscribeRTDB = () => {};
    try {
      const realtimeRef = ref(rtdb, `user_notifications/${userId}`);
      unsubscribeRTDB = onValue(
        realtimeRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            // Trigger Firestore re-fetch for consistency
          }
        },
        (error) => {
          console.error('[NotificationService] RTDB listener error:', error);
        }
      );
    } catch (rtdbError) {
      console.warn('[NotificationService] RTDB not available, using Firestore only:', rtdbError);
    }

    // Combined cleanup function
    const unsubscribe = () => {
      unsubscribeFirestore();
      unsubscribeRTDB();
      this.listeners.delete(userId);
    };

    this.listeners.set(userId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Send email notification based on notification type
   */
  private async sendEmailNotification(
    userId: string,
    type: NotificationType,
    data: NotificationData,
    content: { title: string; message: string }
  ): Promise<void> {
    try {
      // Get user's email from Firestore
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return;
      }

      const userData = userDoc.data();
      const userEmail = userData.email;
      const userName = userData.displayName || 'there';

      if (!userEmail) {
        return;
      }

      const settingsDoc = await getDoc(doc(db, 'user_settings', userId));
      const settings = settingsDoc.exists() ? settingsDoc.data() as Record<string, unknown> : {};
      const emailNotificationsEnabled = settings.emailNotifications !== false;

      if (!emailNotificationsEnabled) {
        return;
      }

      // Send appropriate email based on notification type
      switch (type) {
        case 'new_reaction':
          if (data.reactorName && data.reactionType && data.postId) {
            await EmailAPI.sendNewReactionEmail(
              userEmail,
              userName,
              data.reactorName,
              data.reactionType,
              `https://intouchsn.com/post/${data.postId}`
            );
          }
          break;

        case 'new_comment':
        case 'comment_reply':
          if (data.commenterName && data.commentText && data.postId) {
            await EmailAPI.sendNewCommentEmail(
              userEmail,
              userName,
              data.commenterName,
              data.commentText,
              `https://intouchsn.com/post/${data.postId}`
            );
          }
          break;

        case 'new_message':
          // Messages are handled in the chat component for better preview
          break;

        case 'post_mention': {
          const mentionNotificationsEnabled = settings.notifyMentions !== false;
          if (!mentionNotificationsEnabled) {
            break;
          }

          const postUrl = data.actionUrl
            ? (data.actionUrl.startsWith('http') ? data.actionUrl : `https://intouchsn.com${data.actionUrl}`)
            : data.postId
              ? `https://intouchsn.com/post/${data.postId}`
              : 'https://intouchsn.com';

          await EmailAPI.sendMentionEmail(
            userEmail,
            userName,
            data.authorName || 'Someone',
            data.commentText || 'You were mentioned in a comment.',
            postUrl
          );
          break;
        }

        case 'friend_request':
          if (data.senderName && data.senderId) {
            await EmailAPI.sendFriendRequestEmail(
              userEmail,
              userName,
              data.senderName,
              `https://intouchsn.com/profile/${data.senderId}`
            );
          }
          break;

        case 'group_invite':
          if (data.senderName && data.groupName && data.actionUrl) {
            await EmailAPI.sendGroupInviteEmail(
              userEmail,
              userName,
              data.senderName,
              data.groupName,
              `https://intouchsn.com${data.actionUrl}`
            );
          }
          break;

        case 'page_invite':
          if (data.senderName && data.pageName && data.actionUrl) {
            await EmailAPI.sendPageInviteEmail(
              userEmail,
              userName,
              data.senderName,
              data.pageName,
              `https://intouchsn.com${data.actionUrl}`
            );
          }
          break;

        case 'friend_accepted':
          // Could add a friend accepted email if desired
          console.log('[NotificationService] Friend accepted notification (no email template)');
          break;

        case 'system':
          if (content.title && content.message) {
            await EmailAPI.sendSystemNotification(
              userEmail,
              userName,
              content.title,
              content.message
            );
          }
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('[NotificationService] Error sending email notification:', error);
      // Don't throw - email is nice-to-have, not critical
    }
  }

  /**
   * Trigger push notification (placeholder for future implementation)
   */
  private async triggerPushNotification(
    userId: string,
    notification: { title: string; message: string; data: any }
  ): Promise<void> {
    // TODO: Implement actual push notifications using FCM or similar service
    void userId;
    void notification;
  }

  /**
   * Clean up all listeners
   */
  cleanup(): void {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners.clear();
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Export helper functions for backward compatibility
export async function addNotification(
  userId: string,
  type: NotificationType,
  data: NotificationData
): Promise<string> {
  return notificationService.createNotification({ userId, type, data });
}

export async function markNotificationAsRead(
  userId: string,
  notificationId: string
): Promise<void> {
  return notificationService.markAsRead(userId, notificationId);
}
